// PR-AG-F6.2 — /settings/integrations
// User-facing page to connect/disconnect Google Calendar (Agency Layer v2).
// Backend (edge agency-oauth-google) is fail-closed until 4 secrets are
// configured — UI surfaces 503 oauth_not_configured as a yellow banner.

import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Calendar, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

interface OAuthRow {
  id: string;
  created_at: string;
  scopes: string[] | null;
}

const SCOPE_LABELS: Record<string, string> = {
  "https://www.googleapis.com/auth/calendar": "Eventos",
  "https://www.googleapis.com/auth/userinfo.email": "Email",
  openid: "Identidade",
};

function humanizeScopes(scopes: string[] | null): string {
  if (!scopes || scopes.length === 0) {
    return "Disponibilidade · Eventos · Email";
  }
  const labels = scopes
    .map((s) => SCOPE_LABELS[s])
    .filter(Boolean) as string[];
  // Always include availability conceptually (calendar scope covers it).
  if (labels.includes("Eventos") && !labels.includes("Disponibilidade")) {
    labels.unshift("Disponibilidade");
  }
  return labels.length > 0 ? labels.join(" · ") : "Calendar (acesso completo)";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Integrations() {
  const { user, session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [row, setRow] = useState<OAuthRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [oauthUnavailable, setOauthUnavailable] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("user_oauth_tokens")
      .select("id, created_at, scopes")
      .eq("user_id", user.id)
      .eq("provider", "google")
      .is("revoked_at", null)
      .maybeSingle();
    if (error) {
      // Fail-open: assume "not connected", surface a soft toast.
      console.warn("[integrations] query error", error.message);
      setRow(null);
    } else {
      setRow((data as OAuthRow | null) ?? null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ?status=connected → toast + scrub URL
  useEffect(() => {
    if (searchParams.get("status") === "connected") {
      toast.success("Google Calendar conectado com sucesso!");
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleConnect = useCallback(async () => {
    if (!session?.access_token) {
      toast.error("Sessão expirada. Faça login novamente.");
      return;
    }
    setActionInFlight(true);
    setOauthUnavailable(false);
    try {
      // PR-OAUTH-AUTHORIZE-JSON-CONTRACT-1 (14/05): edge agora retorna JSON
      // com authorize_url em vez de 302 (CORS bloqueia leitura de Location
      // em cross-origin redirect manual). Lemos campo + navegamos.
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/agency-oauth-google?action=authorize`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.status === 503) {
        const body = await res.json().catch(() => ({}));
        if (body?.error === "oauth_not_configured") {
          setOauthUnavailable(true);
          return;
        }
        toast.error("Google OAuth ainda não configurado. Contate o admin.");
        return;
      }
      if (res.status === 401) {
        toast.error("Sessão inválida. Faça login novamente.");
        return;
      }
      if (!res.ok) {
        const body = await res.text().catch(() => "<unreadable>");
        console.error("[Integrations] authorize failed", { status: res.status, body: body.slice(0, 200) });
        toast.error("Não foi possível iniciar a conexão. Tente novamente.");
        return;
      }
      const data = await res.json() as { ok: boolean; authorize_url?: string };
      if (!data.authorize_url) {
        console.error("[Integrations] missing authorize_url in response", data);
        toast.error("Resposta inesperada do servidor.");
        return;
      }
      window.location.href = data.authorize_url;
    } catch (e) {
      console.warn("[integrations] connect error", e);
      toast.error("Erro de rede ao tentar conectar.");
    } finally {
      setActionInFlight(false);
    }
  }, [session]);

  const handleDisconnect = useCallback(async () => {
    if (!row) return;
    setActionInFlight(true);
    try {
      const { error } = await supabase
        .from("user_oauth_tokens")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", row.id);
      if (error) {
        toast.error("Não foi possível desconectar. Tente novamente.");
        return;
      }
      toast.success("Google Calendar desconectado.");
      await refresh();
    } finally {
      setActionInFlight(false);
    }
  }, [row, refresh]);

  return (
    <div className="container mx-auto max-w-3xl py-8 px-4 space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Integrações</h1>
        <p className="text-muted-foreground mt-1">
          Conecte serviços externos para que SYRA possa agir em seu nome — sempre com sua autorização explícita.
        </p>
      </header>

      {oauthUnavailable && (
        <Alert variant="default" className="border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle>Integração temporariamente indisponível</AlertTitle>
          <AlertDescription>
            Administrador precisa configurar credenciais OAuth antes da conexão ficar disponível. Tente novamente em instantes.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2 mt-0.5">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Google Calendar
                  {row && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Conectado
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  {row
                    ? `Conectado em ${formatDate(row.created_at)}`
                    : "SYRA usa seu calendário pra encontrar horários livres em reuniões e, com sua autorização explícita, criar/cancelar eventos quando você aprovar."}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md bg-muted/50 p-3 text-sm">
            <p className="font-medium mb-1">
              {row ? "Permissões ativas" : "Permissões solicitadas"}
            </p>
            <p className="text-muted-foreground">
              {row
                ? humanizeScopes(row.scopes)
                : "Visualizar disponibilidade (free/busy) · Criar eventos · Cancelar eventos · Identificar conta conectada"}
            </p>
          </div>

          {loading ? (
            <Button disabled variant="outline">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando…
            </Button>
          ) : row ? (
            <Button
              variant="outline"
              onClick={handleDisconnect}
              disabled={actionInFlight}
            >
              {actionInFlight && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Desconectar
            </Button>
          ) : (
            <Button onClick={handleConnect} disabled={actionInFlight}>
              {actionInFlight && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Conectar Google Calendar
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
